import { useState, useEffect, useRef } from "react";
import PageLayout from "@/components/feature/PageLayout";
import DiagonalLines from "@/components/feature/DiagonalLines";
import HomeFooter from "@/pages/home/components/HomeFooter";
import PageHero from "@/components/feature/PageHero";
import { useNavigate } from "react-router-dom";

const faqs = [
  { q: "料金はどのくらいかかりますか？", a: "サービス内容によって異なりますが、ホームページ制作は初期費用＋月額のサブスクリプション形式でご提供しています。「高額を一括で払って終わり」ではなく、運用・改善までサポートする料金体系です。まずはお気軽にご相談ください。" },
  { q: "契約期間の縛りはありますか？", a: "サービス内容によって異なりますが、基本的に12ヶ月契約〜となっております。継続的なSEO対策や運用改善には一定の期間が必要なため、短期間での成果をお約束するモデルではなく、中長期での事業成長を一緒に目指す形をとっています。" },
  { q: "途中で解約することはできますか？", a: "中途解約は可能ですが、契約書に基づき残月分のご精算が発生します。長期でお付き合いいただくことを前提としたサービスのため、ご不明な点は契約前にしっかりご説明します。" },
  { q: "複数のサービスをまとめてお願いできますか？", a: "はい、可能です。ホームページ制作・SEO・MEO・広告運用など、複数施策をまとめてご依頼いただくことで、戦略が一貫し成果につながりやすくなります。パッケージでのご相談も歓迎です。" },
  { q: "どのくらいで効果が出ますか？", a: "SEOやMEOは一般的に3〜6ヶ月で変化が現れ始めるケースが多いです。一方、リスティング広告はスタート直後から問い合わせにつながることもあります。施策の組み合わせと事業フェーズに合わせて、最適なプランをご提案します。" },
  { q: "「サイトを作ったけど集客できなかった」という経験があります。今度こそ成果は出ますか？", a: "多くのご相談者が同じ経験をお持ちです。クラプリックスでは制作前にターゲット・競合・キーワードを徹底分析し、「成果から逆算したサイト設計」を行います。作って終わりではなく、公開後の効果について設計することがクラプリックスの強みです。" },
  { q: "効果が出なかった場合はどうなりますか？", a: "集客施策はサイトの内容・競合状況・市場環境など、制作側だけではコントロールできない変動要素が多く、特定の成果を100%保証することは難しいのが実情です。ただし、成果が出やすい設計と戦略をご提供することに全力を尽くしており、その品質には責任を持ってお届けします。" },
];

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("revealed"); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

function HeroSection() {
  return (
    <PageHero
      tag="FREQUENTLY ASKED QUESTIONS"
      title="FAQ"
      sub="よくあるご質問にお答えします"
      bgWord="FAQ"
      bgImage="https://readdy.ai/api/search-image?query=macro%20photograph%20of%20a%20geometric%20crystal%20formation%20with%20warm%20golden%20amber%20light%20refracting%20through%20sharp%20angular%20facets%20prismatic%20warm%20light%20patterns%20translucent%20quartz%20crystal%20cluster%20on%20soft%20cream%20background%20warm%20golden%20light%20dispersion%20and%20internal%20reflections%20scientific%20beauty%20and%20clarity%20fine%20art%20mineral%20photography%20with%20soft%20warm%20studio%20lighting&width=1400&height=600&seq=hero-faq-gold-v1&orientation=landscape"
    />
  );
}

function FaqItem({ faq, idx, isOpen, onToggle }: {
  faq: typeof faqs[0];
  idx: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(24px)";
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setTimeout(() => {
          if (!el) return;
          el.style.transition = "opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1), border-color 0.3s";
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
        }, idx * 70);
        obs.unobserve(el);
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [idx]);

  return (
    <div
      ref={ref}
      className="overflow-hidden"
      style={{ border: `1px solid ${isOpen ? "#c00000" : "#e0e0e0"}`, transition: "border-color 0.3s" }}
    >
      <button
        className="w-full flex items-start gap-3 md:gap-4 px-5 md:px-6 py-5 md:py-6 text-left cursor-pointer focus:outline-none"
        style={{ background: isOpen ? "#f5f5f5" : "#ffffff", transition: "background 0.3s" }}
        onClick={onToggle}
      >
        <div
          className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center shrink-0 mt-0.5"
          style={{ background: isOpen ? "#c00000" : "#f0f0f0", transition: "background 0.3s, transform 0.3s" }}
        >
          <span
            className="text-sm md:text-lg font-bold"
            style={{ color: isOpen ? "#ffffff" : "#111111", fontFamily: "'Barlow', sans-serif", transition: "color 0.3s" }}
          >Q</span>
        </div>
        <span className="flex-1 font-bold text-sm md:text-lg leading-relaxed pt-1" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
          {faq.q}
        </span>
        <i
          className="ri-arrow-down-s-line text-2xl md:text-3xl shrink-0 mt-0.5"
          style={{ color: "#111111", transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </button>
      <div className={`faq-answer ${isOpen ? "open" : ""}`} style={{ background: "#f5f5f5" }}>
        <div className="flex items-start gap-3 md:gap-4 px-5 md:px-6 py-5 md:py-6">
          <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center shrink-0" style={{ background: "#f0f0f0" }}>
            <span className="text-sm md:text-lg font-bold" style={{ fontFamily: "'Barlow', sans-serif", color: "#111111" }}>A</span>
          </div>
          <p className="flex-1 text-xs md:text-base leading-loose pt-1" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
            {faq.a}
          </p>
        </div>
      </div>
    </div>
  );
}

function CtaSection() {
  const ref = useReveal(0.15);
  const navigate = useNavigate();
  return (
    <section className="py-10 md:py-20 px-4 md:px-6" style={{ background: "#f5f5f5", borderTop: "1px solid #e0e0e0" }}>
      <div ref={ref} className="reveal max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="text-sm tracking-[0.4em] font-medium" style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}>CONTACT</span>
          <h2 className="text-2xl md:text-3xl font-bold mt-3" style={{ fontFamily: "'Barlow', sans-serif", color: "#111111" }}>お問い合わせ</h2>
          <div className="mx-auto mt-4" style={{ width: "32px", height: "2px", background: "#c00000" }} />
          <p className="text-xs md:text-sm leading-loose mt-5 mx-auto whitespace-nowrap" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
            ご不明点などございましたらお気軽にお問い合わせください。
          </p>
        </div>

        {/* Contact Options */}
        <div className="grid grid-cols-2 gap-2 md:gap-6">
          {/* Contact Form */}
          <div
            className="flex flex-col items-center text-center p-3 md:p-8 cursor-pointer group"
            style={{ background: "#ffffff", border: "1px solid #e0e0e0", transition: "border-color 0.3s, transform 0.3s" }}
            onClick={() => navigate("/contact")}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#c00000"; e.currentTarget.style.transform = "translateY(-4px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-4" style={{ background: "#f0f0f0", transition: "background 0.3s" }}>
              <i className="ri-mail-line text-base md:text-xl" style={{ color: "#111111" }} />
            </div>
            <h3 className="font-bold text-sm md:text-base mb-1 md:mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>MAIL</h3>

            <span className="text-sm font-medium flex items-center gap-1" style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}>
              <span className="hidden md:inline">フォームを開く</span> <i className="ri-arrow-right-line" />
            </span>
          </div>

          {/* LINE */}
          <a
            href="https://lin.ee/tP9rADP"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center text-center p-3 md:p-8 group"
            style={{ background: "#ffffff", border: "1px solid #e0e0e0", transition: "border-color 0.3s, transform 0.3s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#06C755"; e.currentTarget.style.transform = "translateY(-4px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-4" style={{ background: "#06C755" }}>
              <i className="ri-line-fill text-base md:text-xl" style={{ color: "#ffffff" }} />
            </div>
            <h3 className="font-bold text-sm md:text-base mb-1 md:mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>LINE</h3>

            <span className="text-sm font-medium flex items-center gap-1" style={{ fontFamily: "'Barlow', sans-serif", color: "#06C755" }}>
              <span className="hidden md:inline">LINEを開く</span> <i className="ri-external-link-line" />
            </span>
          </a>


        </div>
      </div>
    </section>
  );
}

export default function FaqPage() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <PageLayout>
      <HeroSection />
      <section className="py-16 md:py-28 px-4 md:px-6 lg:px-20" style={{ background: "#ffffff" }}>
        <div className="max-w-6xl mx-auto flex flex-col gap-12">
          {faqs.map((faq, idx) => (
            <FaqItem
              key={idx}
              faq={faq}
              idx={idx}
              isOpen={open === idx}
              onToggle={() => setOpen(open === idx ? null : idx)}
            />
          ))}
        </div>
      </section>
      <CtaSection />
      <HomeFooter />
      <DiagonalLines variant="gold" intensity="light" />
    </PageLayout>
  );
}
